import clsx from 'clsx';
import { useState, useEffect, memo, useRef } from 'react';

import { useTranslateFn } from '../Translate';

import type { SingleSponsorProps } from './SingleSponsor';
import SingleSponsor from './SingleSponsor';
import SponsorsGroup from './SponsorsGroup';
import { useSponsors } from './useSponsors';

import { useWindowSize } from '@hooks/useWindowSize';
import Image from '@shared/Image';
import type { Sponsor } from '@shared-types/sponsor.types';
import { language } from '@utilities/browser';
import { getColorLightDark } from '@utilities/css-custom-props';
import { imgBaseUrl } from '@utilities/WindowContext';

import * as classes from './styles.module.scss';

export type RandomSponsorsProps = {
  sponsorName?: string;
  extract?: number;
  competitionId?: string;
  showSponsorName?: boolean;
  isFooterSponsor?: boolean;
  typeFilter?: Sponsor['type'] | 'ALL' | 'EXTENDED';
  tagsToSplit?: string[];
  useMainSponsors?: boolean;
  position?: SingleSponsorProps['position'];
  creative?: SingleSponsorProps['creative'];
  mainTitleTag?: 'string';
  globalTitleTag?: 'string';
  localTitleTag?: 'string';
  enableLogo?: boolean;
  direction?: string;
};

/**
 * @param {Object} props
 * @param {string} props.sponsorName - name of the sponsored section (like gotw, perfomancezone)
 * @param {number} props.extract - number of sponsor shown
 * @param {string} props.competitionId - id of the competition
 * @param {boolean} props.showSponsorName - show the name of the sponsor
 * @param {boolean} props.isFooterSponsor - if it is in the footer of the page
 * @param {string} props.typeFilter - filter the type of the sponsor
 * @param {string[]} props.tagsToSplit - tags to split the sponsors
 * @param {boolean} props.useMainSponsors - use the main sponsors
 * @param {string} props.position - position tracking value
 * @param {string} props.creative - c
 * @param {string} props.mainTitleTag tracking value
 * @param {string} props.globalTitleTag - global row title tag
 * @param {string} props.localTitleTag - lo
 * @param {boolean} props.enableLogo - local row title tagcal row title tag
 * @param {string} props.direction - ONLY sets vertical or horizontal for tag and sponsor
 */
const RandomSponsors = (props: RandomSponsorsProps) => {
  const t = useTranslateFn();
  const { isMobile } = useWindowSize();
  const {
    localSponsors,
    globalSponsors,
    othersSponsors,
    taggedSponsors,
    sponsors,
    enableLogo,
    competitionId,
    mainTitleTag,
  } = useSponsors(props);
  const { sponsorName, isFooterSponsor, typeFilter = 'ALL', globalTitleTag, localTitleTag, direction } = props;

  const isSingleSponsor = !!sponsorName;
  const isVertical = direction === 'vertical';

  const container = useRef<HTMLDivElement>(null);
  const [presentedByWordTag, setPresentedByWordTag] = useState<string>('');

  useEffect(() => {
    if (!isSingleSponsor || sponsors.length !== 1 || !container.current) return;
    const [sponsor] = sponsors;

    const sponsoredHeader = container.current.closest<HTMLPkSimpleHeaderElement>('.js-simple-header');
    if (sponsor?.introText?.translations?.[language.toUpperCase()]) {
      setPresentedByWordTag(sponsor.introText.translations[language.toUpperCase()]);
    }

    if (sponsoredHeader) {
      const theme = getColorLightDark(sponsor.colour);
      sponsoredHeader.setAttribute('pk-theme', theme);
      sponsoredHeader.style.setProperty('--pk-simple-header--bg-color', sponsor.colour);
      sponsoredHeader.bgColor = 'simple-header--bg-color';
      if (sponsor.secondaryColour) {
        sponsoredHeader.style.setProperty('--pk-simple-header--title-color', sponsor.secondaryColour);
        sponsoredHeader.titleColor = 'simple-header--title-color';
      }
    }
  }, [isSingleSponsor, sponsorName, sponsors]);

  return (
    <div
      ref={container}
      className={clsx({
        [classes.container]: !isVertical,
        [classes.containerCol]: isVertical,
        [classes.footerSponsors]: isFooterSponsor,
      })}
    >
      {enableLogo && (
        <Image
          className='pk-flex--shrink-0 pk-mr--xs2 pk-pb--xs pk-pb-md--m'
          src={`${imgBaseUrl}/imgml/uefacom/elements/logos/competitions/color/full/${competitionId}` + '.svg'}
          width={isMobile ? 70 : 90}
        />
      )}
      {!!mainTitleTag && <h4 className={classes.title}>{t(mainTitleTag)}</h4>}

      {!!presentedByWordTag && !isFooterSponsor && !isVertical && (
        <div className={classes.presentedBy}>{t(presentedByWordTag)}</div>
      )}
      {!!presentedByWordTag && !isFooterSponsor && isVertical && (
        <div className={classes.presentedByCol}>{t(presentedByWordTag)}</div>
      )}
      {}
      {!isFooterSponsor && !!sponsors.length && (
        <ul className={classes.sponsorList}>
          {sponsors.map((s) => (
            <SingleSponsor key={`custom-sponsor_${s.code}`} sponsor={s} {...props} />
          ))}
        </ul>
      )}
      {isFooterSponsor && (
        <>
          {!!globalSponsors.length && ['ALL', 'EXTENDED', 'GLOBAL'].includes(typeFilter) && (
            <SponsorsGroup
              sponsors={[...globalSponsors, ...(typeFilter === 'EXTENDED' ? othersSponsors : [])]}
              titleTag={globalTitleTag}
              {...props}
            />
          )}
          {!!localSponsors.length && ['ALL', 'EXTENDED', 'LOCAL'].includes(typeFilter) && (
            <SponsorsGroup sponsors={localSponsors} titleTag={localTitleTag} {...props} />
          )}
          {Object.entries(taggedSponsors ?? {}).map(([tag, sponsors]) => (
            <SponsorsGroup key={`tagged-sponsor_${tag}`} sponsors={sponsors} titleTag={`${tag}_sponsors`} {...props} />
          ))}
        </>
      )}
    </div>
  );
};

export default memo(RandomSponsors);
